import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concatMap,
  map,
  of,
  withLatestFrom,
} from 'rxjs';
import { AutoLoginSettings } from '../../@shared/models/auto-login-settings.model';
import { PublicPartnerApiService } from '../../@shared/services/publicpartnerapi.service';
import { getAuth } from '../auth/auth.selectors';
import { getPanelApiUrl } from '../partner/partner.selectors';
import { User } from '../../@shared/models/user.model';
import { CurrentUserActions } from './current-user.actions';

import * as AccountActions from '../account/account.actions';
import * as AuthActions from '../auth/auth.actions';
import * as UserActions from '../user/user.actions';
import * as CurrentUserSelectors from './current-user.selectors';

@Injectable()
export class CurrentUserEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #api = inject(PublicPartnerApiService);
  readonly #http = inject(HttpClient);

  loadCurrentUser$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        CurrentUserActions.loadCurrentUser,
        AuthActions.validateAuthSuccess
      ),
      withLatestFrom(this.#store.select(getAuth)),
      concatMap(([action, auth]) =>
        this.#api.get<{ user: User }>(`/users/${auth?.user_uuid}`).pipe(
          map((result) => CurrentUserActions.loadCurrentUserSuccess({ user: result.user })),
          catchError((error) =>
            of(CurrentUserActions.loadCurrentUserFailure({ error }))
          )
        )
      )
    )
  );

  // Update current user state, if sth changes for the user
  updateCurrentUser$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(
          UserActions.patchUserSuccess,
          UserActions.toggleFavoriteSuccess,
          UserActions.patchEmailSuccess,
          UserActions.changeUserRoleSuccess,
          UserActions.removeUserFromAccountSuccess,
          UserActions.addUserPartnerRoleSuccess,
          UserActions.patchUserPartnerRoleSuccess,
          UserActions.removeUserPartnerRoleSuccess,
          AccountActions.patchAccountUserSuccess,
          AccountActions.postAccountUserSuccess,
          AccountActions.removeAccountUserSuccess
        ),
        withLatestFrom(this.#store.select(CurrentUserSelectors.uuid)),
        concatMap(([action, uuid]) => {
          if (action.user_uuid === uuid) {
            this.#store.dispatch(CurrentUserActions.loadCurrentUser());
          }

          return of();
        })
      ),
    { dispatch: false }
  );

  updateCurrentUserFromLoadAccountUsers$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AccountActions.loadAccountUsersSuccess),
        withLatestFrom(this.#store.select(CurrentUserSelectors.uuid)),
        concatMap(([action, uuid]) => {
          if (action.data.find((elem) => elem.user_uuid === uuid)) {
            this.#store.dispatch(CurrentUserActions.loadCurrentUser());
          }

          return of();
        })
      ),
    { dispatch: false }
  );

  updateCurrentUserFromCreateAccount$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AccountActions.loadAccountSuccess),
        withLatestFrom(this.#store.select(CurrentUserSelectors.uuid)),
        concatMap(([action, uuid]) => {
          if (action.data.owner_uuid === uuid) {
            this.#store.dispatch(CurrentUserActions.loadCurrentUser());
          }

          return of();
        })
      ),
    { dispatch: false }
  );

  updateCurrentUserFromDeleteAccount$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AccountActions.deleteAccountSuccess),
        withLatestFrom(this.#store.select(CurrentUserSelectors.user)),
        concatMap(([action, user]) => {
          const hasAccountRelation = user?.relations?.contracts?.find(
            (elem) => elem.contract_uuid === action.account_uuid
          );

          if (hasAccountRelation) {
            this.#store.dispatch(CurrentUserActions.loadCurrentUser());
          }

          return of();
        })
      ),
    { dispatch: false }
  );

  loadCurrentUserAutoLoginSettings$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        CurrentUserActions.loadCurrentUser,
        CurrentUserActions.loadCurrentUserAutoLoginSettings
      ),
      withLatestFrom(
        combineLatest([
          this.#store.select(getAuth),
          this.#store.select(getPanelApiUrl),
        ])
      ),
      concatMap(([action, [auth, panelApiUrl]]) =>
        this.#http
          .get<AutoLoginSettings>(
            `${panelApiUrl}/users/${auth?.user_uuid}/auto-login-settings`
          )
          .pipe(
            map((result) =>
              CurrentUserActions.loadCurrentUserAutoLoginSettingsSuccess({
                settings: result,
              })
            ),
            catchError((error) =>
              of(
                CurrentUserActions.loadCurrentUserAutoLoginSettingsFailure({
                  error,
                })
              )
            )
          )
      )
    )
  );

  patchCurrentUserAutoLoginSettings$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(CurrentUserActions.patchCurrentUserAutoLoginSettings),
      withLatestFrom(
        combineLatest([
          this.#store.select(getAuth),
          this.#store.select(getPanelApiUrl),
        ])
      ),
      concatMap(([action, [auth, panelApiUrl]]) =>
        this.#http
          .patch<AutoLoginSettings>(
            `${panelApiUrl}/users/${auth?.user_uuid}/auto-login-settings`,
            action.settings
          )
          .pipe(
            map((result) =>
              CurrentUserActions.patchCurrentUserAutoLoginSettingsSuccess({
                settings: result,
              })
            ),
            catchError((error) =>
              of(
                CurrentUserActions.patchCurrentUserAutoLoginSettingsFailure({
                  error,
                })
              )
            )
          )
      )
    )
  );
}
